import { mergeStyles, PrimaryButton, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import * as microsoftTeams from '@microsoft/teams-js';
import * as React from 'react';
import { SMCTicketing } from '../../../../web/models/constants/getHelpConstants';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { GetAuthTokenPurpose } from '../../../../web/models/getHelp/getAuthTokenPurpose';
import { AuthTokenRequest, EmeraldSigninType, TokenType } from '../../../../web/models/getHelp/getHelpAuthToken';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { captureContentUpdate, capturePageAction } from '../../../../web/utils/oneDsUtil';
import { isGetHelpSourceApp } from '../../../../web/utils/statelessUtil';
import { getString } from '../../../../web/utils/stringLoader';
import { AlchemyResult, AuthInfo, UserInfo } from '../../../models/apiModels';
import { AutomationIds } from '../../../models/constants/automationIds';
import {
  Environment,
  SourceApps,
  URLs,
} from '../../../models/constants/constants';
import {
  IUITabViewInfo,
  UITabView,
  UITabViewInfo,
  UITabViewMode,
} from '../../../models/reduxState';
import { ClientAuthProvider } from '../../../utilities';
import { UnAuthSignInLinkContainer } from '../containerComponents/UnAuthSignInLinkContainer';
import { FreelanceRequest, ErrorCodes } from '../models/requestModels';
import { getCommonClassNames, getCommonStyles } from '../styles/commonStyles';
import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import { getContactSupportButtonStyles, getGetHelpStickyFooterContainerStackStyles, getHelpFooterButtonStyles, getHelpStickyFooterContentStackStyles } from '../styles/footerStyles';
import { IStyledComponent } from '../styles/themesUI';
import { OcvComponentContainer } from '../containerComponents/OcvComponentContainer';
import { buildAuthTokenRequest } from '../../../../web/utils/getHelputils';
import { collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { LogType, LogHint } from '../../../../web/models/emeraldLoggingRequest'; 

export interface ContactUsFooterProps {
  showOcv: boolean;
  isSignedIn: boolean;
  userIsSigningIntoClientApp: boolean;
  appVer: string;
  sourceApp: SourceApps;
  namespace: string;
  market: string;
  isClientApp: boolean;
  fixedStickyFooter: boolean;
  redirectForSignInLink: string;
  correlationId: string;
  environment: Environment;
  showFooter: boolean;
  userInfoFetchEnded: boolean;
  userInfo: UserInfo;
  isRequestDetailsEditMode: boolean;
  currentUITabView: UITabView | null;
  isRequestClosed: boolean;
  isRequestSaveBtnEnabled: boolean;
  isUpdatingRequest: boolean;
  isRequestDetailsEditError: boolean;
  isBATenant: boolean;
  hideContactUsButton: boolean;
  isMsa: boolean;
  isAad: boolean;
  isTeams: boolean;
  isGetHelp: boolean;
  isCurrentPageSignIn: boolean;
  isBusinessAssistUXEnabled: boolean;
  dialogId: string;
  themeRoutingResponse: AlchemyResult;
  isContactSupportButtonDisabled: boolean;

  // dispatchers
  dispatchSignInUser: () => void;
  dispatchSignInUserIntoClientApp: () => void;
  dispatchRedirectForSignInLink: () => void;
  setUserIsSigningIntoClientApp: (status: boolean) => void;
  updateUITabViewInfo: (uiTabViewInfo: IUITabViewInfo) => void;
  setAuthInfoForTeams: (authInfo: AuthInfo) => void;
  dispatchUserInfoFetch: () => void;
  dispatchUpdateRequest: () => void;
  setDirtyRequest: (request: FreelanceRequest) => void;
  resetRequestDetailsEditError: () => void;
  setAttachmentError: (error: ErrorCodes) => void;
  signInUserIntoClientApp: () => void;
  resetContactUsFocus: (value: boolean) => void;
  dispatchBypassThemeRouting: (dialogId: string) => void;
}

type IContactUsFooterProps = ContactUsFooterProps & IStyledComponent<IContactUsStyles>;

export class ContactUsFooter extends React.Component<IContactUsFooterProps> {
  private signInFromFooter = false;
  private footerWasVisible = true;
  private firstRender = true;
  private signInRenderLogged = false;
  private redirectLinkFetchedByFooter = false;

  public componentDidMount = () => {
    if (!this.props.redirectForSignInLink && !(this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2)) {
      this.props.dispatchRedirectForSignInLink();
    }
    if (
      this.props.sourceApp !== SourceApps.Teams &&
      this.props.sourceApp !== SourceApps.Teams2 &&
      this.props.sourceApp !== SourceApps.TeamsAdmin &&
      this.props.sourceApp !== SourceApps.TeamsAdmin2 &&
      this.props.sourceApp !== SourceApps.SurfaceApp &&
      this.props.sourceApp !== SourceApps.SurfaceApp2 &&
      this.props.sourceApp !== SourceApps.OfficeDotCom &&
      this.props.sourceApp !== SourceApps.OutlookAdmin &&
      !isGetHelpSourceApp(this.props.sourceApp)
    ) {
      window.addEventListener('scroll', this.handleScroll);
      document.addEventListener('DOMNodeInserted', this.handleScroll);
      this.handleScroll();
    }
  };

  public componentWillUnmount = () => {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('DOMNodeInserted', this.handleScroll);
  };

  public componentDidUpdate = (prevProps: IContactUsFooterProps) => {
    if (
      !prevProps.isSignedIn &&
      this.props.isSignedIn &&
      this.signInFromFooter
    ) {
      this.changeTab(UITabView.requestCreation);
    }

    if (
      !prevProps.showFooter &&
      this.props.showFooter
    ) {
      getHelpLogger.logShowContactSupportUX(this.props.isSignedIn);
      collectTelemetry({
        ContentId: 'ShowContactUsFooter', Type: LogType.ContentUpdate,
        Hint: LogHint.ShowContactUsFooter, Details: { 'isSignedIn': this.props.isSignedIn }
      });
    }

    if (this.props.userIsSigningIntoClientApp) {
      this.props.dispatchSignInUserIntoClientApp();
    }
    // Check if all three APIs fail, if all fail, stay in edit mode and display error message.
    // request edit is done, change to readOnly mode
    if (!this.props.isUpdatingRequest && prevProps.isUpdatingRequest && !this.props.isRequestDetailsEditError) {
      this.props.setAttachmentError(null);
      this.changeTab(UITabView.requestDetails, UITabViewMode.readOnly);
    }
    // login window will be opened only after redirectForSignInLink is fetched
    // This logic was added to pass the user's query to the redirectUri, where the entire page is reloaded after sign in
    if (
      this.redirectLinkFetchedByFooter &&
      !!this.props.redirectForSignInLink &&
      this.props.redirectForSignInLink !== prevProps.redirectForSignInLink
    ) {
      this.redirectLinkFetchedByFooter = false;
      window.open(this.props.redirectForSignInLink, '_self');
    }
  };

  public render = () => {
    return this.props.showFooter ? this.renderFooter() : null;
  };

  public renderFooter = () => {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme, isTeams: this.props.isTeams, sourceApp: this.props.sourceApp });
    const commonClassNames = getCommonClassNames(getCommonStyles);
    if (this.props.userInfoFetchEnded && !this.signInRenderLogged) {
      this.logSignInOptionRendered(!this.props.isSignedIn, 160);
      this.signInRenderLogged = true;
    }

    return this.props.isGetHelp ? (
      <Stack styles={getGetHelpStickyFooterContainerStackStyles(this.props.theme)} >
        <Stack horizontal styles={getHelpStickyFooterContentStackStyles} tokens={{ childrenGap: 16 }}>
          {this.getFooterComponents()}
        </Stack>
      </Stack>
    ) : (
      <div
        id="contactUsFooter"
        className={
          this.props.fixedStickyFooter
            ? mergeStyles(contactUsClassNames.contactUsFooter, contactUsClassNames.stickyFooterFixed, commonClassNames.positionFixed)
            : mergeStyles(contactUsClassNames.contactUsFooter, commonClassNames.positionAbsolute)
        }
      >
        <div className={mergeStyles(contactUsClassNames.row, contactUsClassNames.stickyFooterRow)}>
          <div className={mergeStyles('col-lg-3', 'col-xs-0', contactUsClassNames.col)} />
          <div className={mergeStyles('col-lg-7', 'col-xs-12', contactUsClassNames.col)}>
            <div className={contactUsClassNames.contactUsFooterBtnArea}>
              <div className={mergeStyles('col-lg-10', contactUsClassNames.col)}>{this.getFooterComponents()}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  public onContactSupportClick = () => {
    // send message to the Surface app to log the event
    if (this.props.sourceApp === SourceApps.SurfaceApp && window && window.chrome && window.chrome.webview && window.chrome.webview.postMessage ){
      window.chrome.webview.postMessage("ContactSupportButtonClicked");
    }
    const logHandlerTags = {
      content: {
        contentId: 'stickyFooterContactSupportBtn',
        contentName: 'stickyFooterContactSupport',
        correlationId: this.props.correlationId,
        formnm: 'maxFunnel',
        fileName: 'ContactUsFooter.tsx',
        methodName: 'onContactSupportClick',
        areaName: 'modalitiesSection',
      },
      behavior: 'CLICK',
    };
    capturePageAction(null, logHandlerTags);
    if (this.props.themeRoutingResponse.Insights?.some((insight) => insight.SolutionType === 'Branching') && this.props.dialogId !== '' && this.props.themeRoutingResponse.ThemeRoutingData != null) {
      this.props.dispatchBypassThemeRouting(this.props.dialogId);
    }
    this.changeTab(UITabView.requestCreation);
    this.props.resetContactUsFocus(true);
  };

  public onRequestDetailsBtnClick = (action: string) => () => {
    if (!action) {
      return;
    }
    const contentId = `stickyFooterRequestDetails${action}Btn`;
    switch (action) {
      case 'Edit':
        this.changeTab(UITabView.requestDetails, UITabViewMode.edit);
        this.props.resetContactUsFocus(true);
        break;
      case 'Save':
        this.props.dispatchUpdateRequest();
        break;
      case 'Cancel':
        this.changeTab(UITabView.requestDetails, UITabViewMode.readOnly);
        this.props.resetRequestDetailsEditError();
        this.props.setAttachmentError(null);
        this.props.setDirtyRequest(null);
        break;
      default:
        break;
    }
    const logHandlerTags = {
      content: {
        contentId: contentId,
        contentName: contentId,
        correlationId: this.props.correlationId,
        formnm: 'maxFunnel',
        fileName: 'ContactUsFooter.tsx',
        methodName: 'onRequestDetailsBtnClick',
        areaName: 'modalitiesSection',
      },
      behavior: 'CLICK',
    };
    capturePageAction(null, logHandlerTags);
  };

  public changeTab = (uITabView: UITabView, viewMode?: UITabViewMode) => {
    const uiTabViewInfo = new UITabViewInfo();
    uiTabViewInfo.uITabView = uITabView;
    if (viewMode) {
      uiTabViewInfo.viewMode = viewMode;
    }
    uiTabViewInfo.showAppBarBackButton = this.props.isGetHelp;
    this.props.updateUITabViewInfo(uiTabViewInfo);
  };

  public browserSignInClickHandler = () => {
    if (this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2) {
      this.redirectLinkFetchedByFooter = true;
      this.props.dispatchRedirectForSignInLink();
    } else {
      window.open(
        this.props.redirectForSignInLink,
        '_blank',
        'width=500,height=700'
      );
    }
    this.props.dispatchSignInUser();
  };

  public clientAppSignInClickHandler = () => {
    this.props.setUserIsSigningIntoClientApp(true);
  };

  public onSignInClick = () => {
    const logHandlerTags = {
      content: {
        contentId: 'stickyFooterSignInBtn',
        contentName: 'stickyFooterSignIn',
        correlationId: this.props.correlationId,
        formnm: 'maxFunnel',
        fileName: 'ContactUsFooter.tsx',
        methodName: 'onSignInClick',
        areaName: 'modalitiesSection',
      },
      behavior: 'SIGNIN',
    };
    capturePageAction(null, logHandlerTags);

    getHelpLogger.logSignInButtonClicked();

    if (
      this.props.sourceApp === SourceApps.Teams ||
      this.props.sourceApp === SourceApps.Teams2 ||
      this.props.sourceApp === SourceApps.TeamsAdmin ||
      this.props.sourceApp === SourceApps.TeamsAdmin2
    ) {
      microsoftTeams.authentication.authenticate({
        url: window.location.origin + URLs.TeamsAuthStart,
        width: 600,
        height: 535,
        successCallback: (result) => {
          const authInfo: AuthInfo = {
            provider: ClientAuthProvider.AAD,
            token: result,
          };
          this.props.setAuthInfoForTeams(authInfo);
          this.props.dispatchUserInfoFetch();
          this.signInFromFooter = true;
        },
        failureCallback: (reason) => {
          console.log('Login failed: ' + reason);
        },
      });
    } else if (isGetHelpSourceApp(this.props.sourceApp)) {
      const authCommand: AuthTokenRequest = buildAuthTokenRequest(GetAuthTokenPurpose.SignInAndConsumerCategoryOptions, EmeraldSigninType.ForceSignin);
      notifyParent(JSON.stringify(authCommand));
      this.props.signInUserIntoClientApp();
      this.signInFromFooter = true;
    } else {
      this.props.isClientApp
        ? this.clientAppSignInClickHandler()
        : this.browserSignInClickHandler();
      this.signInFromFooter = true;
      this.props.resetContactUsFocus(true);
    }
  };

  private getFooterComponents = () => {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme, isTeams: this.props.isTeams, sourceApp: this.props.sourceApp });
    const footerStyles = getContactSupportButtonStyles({
      theme: this.props.theme,
      sourceApp: this.props.sourceApp,
      isTeams: this.props.isTeams,
    });
    let body = [];
    const isSignedIn = this.props.isSignedIn;
    const currentUITabView = this.props.currentUITabView;
    if (this.props.userIsSigningIntoClientApp) {
      body.push(<div key={'footer-loader'} className='loader' />);
    } else if (currentUITabView === UITabView.searchHelp || currentUITabView === UITabView.requestCreation) {
      // add 'sign in to Contact Support' or 'Contact Support' button
      if (!this.props.hideContactUsButton) {
        let btnText;
        if (isSignedIn) {
          btnText = this.props.isBATenant && this.props.isBusinessAssistUXEnabled
            ? getString('result_businessAssistContactSupportText')
            : getString('modality_contactSupportText');
        } else {
          btnText = getString('result_signInContactSupportText');
        }
        body.push(
          <PrimaryButton
            key={isSignedIn ? 'footer-contact-btn' : 'footer-signin-btn'}
            data-automation-id={isSignedIn ? AutomationIds.FooterContactUsBtn : AutomationIds.FooterSignInBtn}
            styles={this.props.isGetHelp ? getHelpFooterButtonStyles : footerStyles}
            onClick={this.onBtnClick(isSignedIn)}
            text={btnText}
            disabled={this.props.isContactSupportButtonDisabled}
          />
        );
      }
      // add feedback button
      if (this.props.showOcv) {
        body.push(
          <div
            key={'footer-ocv-link-container'}
            className='float-right'
            data-automation-id={AutomationIds.GiveUsFeedbackBtn}
          >
            <OcvComponentContainer />
          </div>
        );
      }
      // add unauth sign in link for branching
      if (!isSignedIn && !(currentUITabView === UITabView.requestCreation && this.props.isCurrentPageSignIn)) {
        getHelpLogger.logUnAuthLinkDisplayed();
        body.push(<UnAuthSignInLinkContainer key={'footer-UnAuthSignInLinkContainer'} />);
      }
    } else if (
      isSignedIn &&
      this.props.currentUITabView === UITabView.requestDetails
    ) {
      if (this.props.isRequestDetailsEditMode) {
        body.push(
          <div key={'footer-requestdeatils'}>
            {this.props.isUpdatingRequest ? (
              <Spinner
                key={'footer-updatingrequest-spinner'}
                size={SpinnerSize.medium}
                styles={footerStyles.spinner}
              />
            ) : (
              <PrimaryButton
                key={'footer-save-btn'}
                disabled={!this.props.isRequestSaveBtnEnabled}
                styles={footerStyles}
                onClick={this.onRequestDetailsBtnClick('Save')}
                text={getString('requestDetails_saveEditRequest')}
              />
            )}
            <PrimaryButton
              key={'footer-cancel-btn'}
              disabled={this.props.isUpdatingRequest}
              styles={footerStyles}
              onClick={this.onRequestDetailsBtnClick('Cancel')}
              text={getString('requestDetails_cancelEditRequest')}
            />
          </div>
        );
      } else if (!this.props.isRequestClosed) {
        body.push(
          <PrimaryButton
            key={'footer-edit-btn'}
            styles={footerStyles}
            onClick={this.onRequestDetailsBtnClick('Edit')}
            text={getString('requestDetails_editRequest')}
          />
        );
      }
    } else {
      body = null;
    }
    return body;
  };

  private onBtnClick = (isSignedIn: boolean) => () => {
    getHelpLogger.logDockedButtonClicked();
    collectTelemetry({
      ContentId: 'ClickedFooterButton', Type: LogType.PageAction,
      Hint: LogHint.ClickedFooterButton, Details: { 'isSignedIn': isSignedIn }
    });
    isSignedIn ? this.onContactSupportClick() : this.onSignInClick();
  };

  private logSignInOptionRendered = (sendLog: boolean, behavior: number) => {
    if (sendLog) {
      const overrideTags = {
        content: {
          contentName: 'signInOption',
          formnm: 'maxFunnel',
          areaName: 'modalitiesSection',
          fileName: 'ContactUsFooter.tsx',
          methodName: 'logSignInOptionRendered',
        },
        behavior: behavior,
      };
      captureContentUpdate(overrideTags);
    }
  };

  private handleScroll = () => {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme, isTeams: this.props.isTeams, sourceApp: this.props.sourceApp });
    const commonClassNames = getCommonClassNames(getCommonStyles);
    const footerArea = document.getElementById('footerArea');
    if (this.elementInViewport(footerArea)) {
      const stickyFooter = document.getElementById('contactUsFooter');
      if (stickyFooter) {
        if (this.firstRender || !this.footerWasVisible) {
          if (this.firstRender) {
            this.firstRender = false;
          }
          this.footerWasVisible = true;
          stickyFooter.setAttribute('class', mergeStyles(contactUsClassNames.contactUsFooter, commonClassNames.positionAbsolute));
        }
      }
    } else if (this.footerWasVisible) {
      this.footerWasVisible = false;
      if (this.firstRender) {
        this.firstRender = false;
      }
      const stickyFooter = document.getElementById('contactUsFooter');
      if (stickyFooter) {
        stickyFooter.setAttribute('class', mergeStyles(contactUsClassNames.contactUsFooter, contactUsClassNames.stickyFooterFixed, commonClassNames.positionFixed));
      }
    }
  };

  private elementInViewport(el: HTMLElement) {
    if (el) {
      let top = el.offsetTop;
      let left = el.offsetLeft;
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      while (el.offsetParent) {
        el = el.offsetParent as HTMLElement;
        top += el.offsetTop;
        left += el.offsetLeft;
      }

      return (
        top < window.pageYOffset + window.innerHeight &&
        left < window.pageXOffset + window.innerWidth &&
        top + height > window.pageYOffset &&
        left + width > window.pageXOffset
      );
    }
    return false;
  }
}
